import { useNavigate } from 'react-router-dom'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Grid,
  Button,
  Paper,
  Tooltip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import CheckIcon from '@mui/icons-material/Check'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import StorageIcon from '@mui/icons-material/Storage'
import { useComparison } from '../context/ComparisonContext'

const DRAWER_WIDTH = 450

function MetricCard({ label, value, subtext, trend }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {value || 'N/A'}
        </Typography>
        {trend === 'up' && <TrendingUpIcon color="success" fontSize="small" />}
        {trend === 'down' && <TrendingDownIcon color="error" fontSize="small" />}
      </Box>
      {subtext && (
        <Typography variant="caption" color="text.secondary">
          {subtext}
        </Typography>
      )}
    </Paper>
  )
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  )
}

function CaseStudyDetail({ caseStudy, open, onClose }) {
  const navigate = useNavigate()
  const { addItem, hasItem, removeItem } = useComparison()

  if (!caseStudy) return null

  // Navigate to Data Explorer with this study's industry and equipment filters
  const handleViewInExplorer = () => {
    const params = new URLSearchParams()
    if (caseStudy.industry) {
      params.set('industry', caseStudy.industry.toLowerCase().replace(/\s/g, '_'))
    }
    if (caseStudy.equipment_type) {
      params.set('equipment', caseStudy.equipment_type)
    }
    onClose()
    navigate(`/data-explorer?${params.toString()}`)
  }

  const isInComparison = hasItem(caseStudy.id, 'caseStudy')

  const handleToggleCompare = () => {
    if (isInComparison) {
      removeItem(caseStudy.id, 'caseStudy')
    } else {
      addItem({
        id: caseStudy.id,
        type: 'caseStudy',
        data: caseStudy
      })
    }
  }

  const formatCurrency = (val) => {
    if (!val) return 'N/A'
    return `$${val.toLocaleString()}`
  }

  const formatPercent = (val) => {
    if (!val) return 'N/A'
    return `${val.toFixed(1)}%`
  }

  const categoryLabels = {
    field_study: 'Field Study',
    longevity_study: 'Longevity Study',
    supporting_document: 'Supporting Document',
    lab_report: 'Lab Report'
  }

  const qualityColors = {
    complete: 'success',
    partial: 'warning',
    minimal: 'default'
  }

  const qualityTooltips = {
    complete: 'All key fields present: efficiency %, kW/ton before/after, annual savings, PE certification',
    partial: 'Some fields missing: may lack savings data, kW/ton measurements, or detailed equipment info',
    minimal: 'Limited data: only basic efficiency metrics available'
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          p: 0
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2,
        bgcolor: 'secondary.main',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {caseStudy.id}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {caseStudy.source}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Close case study details">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
        {/* Key Metrics */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <MetricCard
              label="Efficiency Improvement"
              value={formatPercent(caseStudy.efficiency_pct)}
              trend="up"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCard
              label="Annual Savings"
              value={formatCurrency(caseStudy.annual_savings)}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCard
              label="Payback Period"
              value={caseStudy.payback_months ? `${caseStudy.payback_months} mo` : 'N/A'}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCard
              label="Data Quality"
              value={caseStudy.quality ? caseStudy.quality.charAt(0).toUpperCase() + caseStudy.quality.slice(1) : 'N/A'}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Overview Section */}
        <Section title="OVERVIEW">
          <DetailRow label="Category" value={categoryLabels[caseStudy.category] || caseStudy.category} />
          <DetailRow label="Industry" value={caseStudy.industry?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
          <DetailRow label="State" value={caseStudy.state} />
          <DetailRow label="Test Date" value={caseStudy.test_date} />
        </Section>

        {/* Equipment Section */}
        <Section title="EQUIPMENT DETAILS">
          <Box sx={{ mb: 1.5 }}>
            <Chip
              label={caseStudy.equipment_type}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          </Box>
          <DetailRow label="Equipment Age" value={caseStudy.equipment_age ? `${caseStudy.equipment_age} years` : null} />
          <DetailRow label="Capacity" value={caseStudy.capacity_tons ? `${caseStudy.capacity_tons} tons` : null} />
        </Section>

        {/* Performance Section */}
        <Section title="PERFORMANCE METRICS">
          <DetailRow label="Efficiency Improvement" value={formatPercent(caseStudy.efficiency_pct)} />
          <DetailRow label="kW/ton Before" value={caseStudy.kw_per_ton_before?.toFixed(3)} />
          <DetailRow label="kW/ton After" value={caseStudy.kw_per_ton_after?.toFixed(3)} />
          {caseStudy.kw_per_ton_before && caseStudy.kw_per_ton_after && (
            <DetailRow
              label="kW/ton Reduction"
              value={(caseStudy.kw_per_ton_before - caseStudy.kw_per_ton_after).toFixed(3)}
            />
          )}
          <DetailRow label="Annual Savings" value={formatCurrency(caseStudy.annual_savings)} />
          <DetailRow label="Payback Period" value={caseStudy.payback_months ? `${caseStudy.payback_months} months` : null} />
        </Section>

        {/* Certification Section */}
        <Section title="CERTIFICATION">
          <DetailRow
            label={caseStudy.certification_type === 'engineering_analysis' ? 'Certified By' : 'PE Name'}
            value={caseStudy.pe_name}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              Data Quality
            </Typography>
            <Tooltip title={qualityTooltips[caseStudy.quality] || 'Data quality level unknown'} arrow>
              <Chip
                label={caseStudy.quality || 'Unknown'}
                size="small"
                color={qualityColors[caseStudy.quality] || 'default'}
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        </Section>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant={isInComparison ? "contained" : "outlined"}
              color={isInComparison ? "success" : "primary"}
              startIcon={isInComparison ? <CheckIcon /> : <CompareArrowsIcon />}
              onClick={handleToggleCompare}
              size="small"
            >
              {isInComparison ? 'Added' : 'Compare'}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<StorageIcon />}
              onClick={handleViewInExplorer}
              size="small"
              aria-label="View similar data in Data Explorer"
            >
              Explorer
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  )
}

export default CaseStudyDetail
